/**
 * Promo popup
 *
 * Opens the campaign banner over the page after a configurable delay and remembers that
 * the visitor has seen it. Everything that decides *whether* the popup may appear at all
 * (the on/off switch, the language, the excluded pages) is decided server side — when the
 * markup is absent this module does nothing.
 */

const FOCUSABLE = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';
const DAY_IN_MS = 24 * 60 * 60 * 1000;
// sessionStorage is per tab, so it alone would show the popup again in every new tab.
// localStorage carries the "already seen" flag across tabs, and this window is what
// makes it a *session* rather than forever — it is pushed forward on every page view,
// so it only lapses once the visitor has actually stopped browsing.
const SESSION_WINDOW_MS = 30 * 60 * 1000;

/**
 * Storage can throw (Safari private mode, cookies blocked). Never let that break the page.
 */
function readStore(store, key) {
    try {
        return window[store].getItem(key);
    } catch (e) {
        return null;
    }
}

function writeStore(store, key, value) {
    try {
        window[store].setItem(key, value);
    } catch (e) {
        // Nothing to do — the visitor simply sees the popup again next time.
    }
}

function seenAt(key) {
    const stamp = parseInt(readStore('localStorage', key), 10);

    return Number.isNaN(stamp) ? 0 : stamp;
}

function alreadySeen(frequency, key) {
    if (frequency === 'every') {
        return false;
    }

    if (frequency === 'day') {
        return Date.now() - seenAt(key) < DAY_IN_MS;
    }

    // This tab always remembers; other tabs of the same session do too, until the window lapses.
    return readStore('sessionStorage', key) === '1' || Date.now() - seenAt(key) < SESSION_WINDOW_MS;
}

function remember(frequency, key) {
    if (frequency === 'every') {
        return;
    }

    writeStore('localStorage', key, String(Date.now()));

    if (frequency === 'session') {
        writeStore('sessionStorage', key, '1');
    }
}

export default function promoPopup() {
    const root = document.querySelector('[data-promo-popup]');

    if (!root) {
        return;
    }

    const dialog = root.querySelector('[data-promo-popup-dialog]');
    const closeButton = root.querySelector('[data-promo-popup-close]');

    if (!dialog || !closeButton) {
        return;
    }

    const storageKey = root.dataset.storageKey || 'ws-promo-popup';
    const frequency = root.dataset.frequency || 'session';
    const parsedDelay = parseFloat(root.dataset.delay);
    const delay = Number.isNaN(parsedDelay) ? 2000 : Math.max(0, parsedDelay) * 1000;

    if (alreadySeen(frequency, storageKey)) {
        // Keep the session alive while the visitor keeps clicking around the site.
        if (frequency === 'session') {
            remember(frequency, storageKey);
        }

        return;
    }

    let lastFocused = null;
    let isOpen = false;

    function trapFocus(event) {
        if (event.key !== 'Tab') {
            return;
        }

        const focusable = Array.from(dialog.querySelectorAll(FOCUSABLE));

        if (focusable.length === 0) {
            return;
        }

        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
        }
    }

    function onKeydown(event) {
        if (event.key === 'Escape' || event.key === 'Esc') {
            event.preventDefault();
            close();
            return;
        }

        trapFocus(event);
    }

    function close() {
        if (!isOpen) {
            return;
        }

        isOpen = false;
        root.classList.remove('is-open');
        document.removeEventListener('keydown', onKeydown, true);

        // Wait for the fade out before taking it out of the accessibility tree.
        window.setTimeout(() => {
            if (!isOpen) {
                root.hidden = true;
            }
        }, 300);

        if (lastFocused && typeof lastFocused.focus === 'function' && document.contains(lastFocused)) {
            lastFocused.focus();
        }
    }

    function open() {
        if (isOpen) {
            return;
        }

        isOpen = true;
        lastFocused = document.activeElement;
        root.hidden = false;

        // Force a reflow so the transition runs from the hidden state.
        void root.offsetWidth;

        root.classList.add('is-open');
        remember(frequency, storageKey);
        document.addEventListener('keydown', onKeydown, true);

        // The overlay is still computing as visibility:hidden on this tick — the fade has
        // not started yet — and focus() is a no-op on a hidden element. Wait for the frame
        // that actually paints it.
        window.requestAnimationFrame(() => {
            window.requestAnimationFrame(() => {
                if (isOpen) {
                    closeButton.focus();
                }
            });
        });
    }

    closeButton.addEventListener('click', (event) => {
        event.preventDefault();
        close();
    });

    // Clicking the dark area around the dialog closes it; clicking the banner does not.
    root.addEventListener('click', (event) => {
        if (event.target === root) {
            close();
        }
    });

    // Following the banner should not leave a popup waiting behind it.
    root.querySelectorAll('[data-promo-popup-link]').forEach((link) => {
        link.addEventListener('click', () => {
            remember(frequency, storageKey);

            // A link that opens in a new tab leaves this page behind, so put the popup
            // away instead of leaving it waiting for the visitor to come back.
            if (link.target === '_blank') {
                close();
            }
        });
    });

    window.setTimeout(open, delay);
}
